/**
 * GenerateMonthlyInvoiceModal
 * Modal for generating a monthly invoice grouping multiple affaires
 *
 * Supports two modes:
 * 1. Contact Mode: Pre-selected billing contact, loads eligible affaires for that contact
 * 2. Client Mode: Search for client, see all eligible affaires grouped by billing contact
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  XMarkIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  GlobeAltIcon,
  ArrowDownTrayIcon,
  PaperAirplaneIcon,
  ArrowLeftIcon,
  MagnifyingGlassIcon,
  UserIcon,
  UsersIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  EyeIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';
import {
  useMonthlyBillingEligible,
  useClientBillingEligible,
  useGenerateMonthlyInvoice,
  useNextInvoiceNumber,
  useValidateInvoice
} from '../../hooks/useInvoices';
import { usePeppolRegistration, useValidateVat } from '../../hooks/useVatValidation';
import { useClientSearch } from '../../hooks/useClientSearch';
import { downloadInvoicePdf } from '../../api/facturation.api';
import { VatStatusSection } from './components/VatStatusSection';
import { PdfPreviewModal } from '../PdfPreviewModal/PdfPreviewModal';
import { useBankAccounts, useAddBankAccount } from '../../hooks/useBankAccounts';
import type { EligibleAffaire, Invoice, VatValidation, BankAccount } from '../../types/facturation.types';
import '../../facturation.css';

// Helper to check if VAT number is a placeholder (not a real VAT)
const isPlaceholderVat = (vat: string | null | undefined): boolean => {
  if (!vat) return true;
  const normalized = vat.trim().toUpperCase();
  // Common placeholder values used in FileMaker when no VAT is available
  return ['NA', 'N/A', 'NEANT', 'NÉANT', 'NONE', '-', '/', ''].includes(normalized);
};

// Get the effective VAT number for validation (null if placeholder)
const getEffectiveVatNumber = (vat: string | null | undefined): string | null => {
  if (isPlaceholderVat(vat)) return null;
  return vat || null;
};

// Local type for billing contact prop (simplified)
interface BillingContact {
  id: string;
  name: string;
  address: string | null;
  vat_number: string | null;
  email: string | null;
  pays: string | null;
}

// Search mode type
type SearchMode = 'contact' | 'client';

interface GenerateMonthlyInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  billingContact?: BillingContact; // Now optional - can start in client search mode
  onSuccess?: () => void;
  initialMode?: SearchMode; // Allow starting in client mode
  preselectedAffaireIds?: string[]; // Pre-selected affaires from GroupFacturationView
}

// Format currency
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('fr-BE', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
};

// Language options
const languages = [
  { code: 'fr', label: 'Francais' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'de', label: 'Deutsch' },
  { code: 'en', label: 'English' },
];

export const GenerateMonthlyInvoiceModal: React.FC<GenerateMonthlyInvoiceModalProps> = ({
  isOpen,
  onClose,
  billingContact: propBillingContact,
  onSuccess,
  initialMode = 'contact',
  preselectedAffaireIds
}) => {
  // Flag to check if affaires are pre-selected (from GroupFacturationView)
  const hasPreselectedAffaires = preselectedAffaireIds && preselectedAffaireIds.length > 0;
  // Mode and search state
  const [searchMode, setSearchMode] = useState<SearchMode>(propBillingContact ? 'contact' : initialMode);
  const [selectedClient, setSelectedClient] = useState<{ id: string; name: string } | null>(null);
  const [expandedContacts, setExpandedContacts] = useState<Set<string>>(new Set());

  // Core state
  const [selectedLanguage, setSelectedLanguage] = useState('fr');
  const [selectedAffaires, setSelectedAffaires] = useState<string[]>([]);
  const [step, setStep] = useState<'search' | 'select' | 'preview' | 'generating' | 'finalizing' | 'success' | 'error'>('select');
  const [errorMessage, setErrorMessage] = useState('');
  const [generatedInvoice, setGeneratedInvoice] = useState<Invoice | null>(null);
  const [isValidatingFalco, setIsValidatingFalco] = useState(false);

  // Store initial affaires snapshot for pre-selected mode (prevents display issues after query refetch)
  const [initialAffairesSnapshot, setInitialAffairesSnapshot] = useState<EligibleAffaire[] | null>(null);

  // Effective billing contact (from prop or selected from client search)
  const [effectiveBillingContact, setEffectiveBillingContact] = useState<BillingContact | null>(
    propBillingContact || null
  );

  // VAT validation state
  const [vatValidationResult, setVatValidationResult] = useState<VatValidation | null>(null);
  const [isVatValidating, setIsVatValidating] = useState(false);
  const [vatOverrideAccepted, setVatOverrideAccepted] = useState(false);

  // PDF preview and email state
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [isPdfPreviewMode, setIsPdfPreviewMode] = useState(false);
  const [sendEmailOnValidate, setSendEmailOnValidate] = useState(true);

  // Bank account state
  const [bankAccountId, setBankAccountId] = useState<number | null>(null);
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [newAccountLabel, setNewAccountLabel] = useState('');
  const [newAccountIban, setNewAccountIban] = useState('');
  const [newAccountBic, setNewAccountBic] = useState('');

  // Client search hook
  const clientSearch = useClientSearch();

  // Hooks for contact mode
  const { data: contactEligibleAffaires, isLoading: isLoadingContactEligible } = useMonthlyBillingEligible(
    effectiveBillingContact?.id || ''
  );

  // Hooks for client mode
  const { data: clientEligibleData, isLoading: isLoadingClientEligible } = useClientBillingEligible(
    selectedClient?.id || ''
  );

  const { data: nextNumber } = useNextInvoiceNumber();
  // Use effective VAT for Peppol (skip placeholder values)
  const { data: peppolStatus } = usePeppolRegistration(getEffectiveVatNumber(effectiveBillingContact?.vat_number) || '');

  const { data: bankAccounts = [] } = useBankAccounts();
  const addBankAccountMutation = useAddBankAccount();

  const generateMutation = useGenerateMonthlyInvoice();
  const validateMutation = useValidateInvoice();
  const vatMutation = useValidateVat();

  // Determine which affaires to show based on mode
  const eligibleAffaires = useMemo(() => {
    if (searchMode === 'contact' && contactEligibleAffaires) {
      return contactEligibleAffaires as EligibleAffaire[];
    }
    if (searchMode === 'client' && clientEligibleData?.billing_contacts) {
      // Flatten affaires from all billing contacts
      return clientEligibleData.billing_contacts.flatMap(bc => bc.affaires);
    }
    return [];
  }, [searchMode, contactEligibleAffaires, clientEligibleData]);

  // Track which billing contacts are selected (for validation)
  const selectedBillingContactIds = useMemo(() => {
    if (searchMode === 'contact') {
      return effectiveBillingContact ? new Set([effectiveBillingContact.id]) : new Set<string>();
    }
    // In client mode, determine which contacts have selected affaires
    if (!clientEligibleData?.billing_contacts) return new Set<string>();

    const contactIds = new Set<string>();
    for (const bc of clientEligibleData.billing_contacts) {
      for (const affaire of bc.affaires) {
        if (selectedAffaires.includes(affaire.affaire_id)) {
          contactIds.add(bc.billing_contact_id);
        }
      }
    }
    return contactIds;
  }, [searchMode, effectiveBillingContact, clientEligibleData, selectedAffaires]);

  // Check if multiple billing contacts are selected (error condition)
  const hasMultipleBillingContacts = selectedBillingContactIds.size > 1;

  // Loading state
  const isLoadingEligible = searchMode === 'contact' ? isLoadingContactEligible : isLoadingClientEligible;

  // Track previous isOpen state to detect when modal actually opens
  const wasOpenRef = useRef(false);

  // Reset state when modal opens (only on actual open transition, not on re-renders)
  useEffect(() => {
    // Only reset when transitioning from closed to open
    if (isOpen && !wasOpenRef.current) {
      setStep(propBillingContact ? 'select' : 'search');
      setSearchMode(propBillingContact ? 'contact' : initialMode);
      setErrorMessage('');
      // Use pre-selected affaires if provided
      setSelectedAffaires(preselectedAffaireIds || []);
      setGeneratedInvoice(null);
      setIsValidatingFalco(false);
      setSelectedClient(null);
      setExpandedContacts(new Set());
      setEffectiveBillingContact(propBillingContact || null);
      setVatValidationResult(null);
      setIsVatValidating(false);
      setVatOverrideAccepted(false);
      setShowPdfPreview(false);
      setIsPdfPreviewMode(false);
      setSendEmailOnValidate(true);
      setInitialAffairesSnapshot(null); // Clear snapshot on modal open
      setShowAddAccount(false);
      // Auto-select default bank account
      const defaultAccount = bankAccounts.find((a: BankAccount) => a.is_default);
      setBankAccountId(defaultAccount?.id || bankAccounts[0]?.id || null);
      clientSearch.reset();
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, propBillingContact, initialMode, preselectedAffaireIds, bankAccounts]);

  // VAT validation trigger when billing contact changes or modal opens
  // Skip validation for placeholder VAT values (NA, N/A, etc.)
  useEffect(() => {
    // Only validate when modal is open, we have a valid VAT, and are on the select step
    if (!isOpen) return;

    const effectiveVatRaw = getEffectiveVatNumber(effectiveBillingContact?.vat_number);
    if (!effectiveVatRaw || step !== 'select') {
      // Don't clear here - the modal reset effect handles clearing on open
      // This prevents race conditions when state is being set up
      return;
    }

    // Normalize VAT number: add country prefix if missing
    // Use pays field from fm_contacts table (via billing contact)
    let effectiveVat = effectiveVatRaw.replace(/[.\s-]/g, '').toUpperCase();

    // If already has 2-letter prefix, use as-is
    if (!/^[A-Z]{2}/.test(effectiveVat)) {
      // Determine prefix from pays field (from fm_contacts table)
      const pays = effectiveBillingContact?.pays;
      const prefix = (pays && /^[A-Z]{2}$/i.test(pays)) ? pays.toUpperCase() : 'BE';

      if (prefix === 'BE') {
        // Belgian-specific: handle 9/10 digit numbers
        if (/^\d{10}$/.test(effectiveVat)) {
          effectiveVat = 'BE' + effectiveVat;
        } else if (/^\d{9}$/.test(effectiveVat)) {
          effectiveVat = 'BE0' + effectiveVat;
        }
      } else if (/^\d+$/.test(effectiveVat)) {
        // For other countries, prepend the prefix to digits-only VAT
        effectiveVat = prefix + effectiveVat;
      }
    }

    setIsVatValidating(true);
    setVatValidationResult(null); // Clear previous result before new validation

    vatMutation.mutate(
      {
        vatNumber: effectiveVat,
        storedName: effectiveBillingContact?.name || undefined,
        storedAddress: effectiveBillingContact?.address || undefined,
      },
      {
        onSuccess: (result) => {
          setVatValidationResult(result as VatValidation);
          setIsVatValidating(false);
        },
        onError: (error) => {
          // Set an error result instead of null so UI can show proper error state
          setVatValidationResult({
            vatNumber: effectiveVat,
            countryCode: effectiveVat.slice(0, 2),
            isValid: false,
            error: error instanceof Error ? error.message : 'Erreur de validation VIES',
            errorType: 'API_ERROR',
            needsReview: false,
          } as VatValidation);
          setIsVatValidating(false);
        },
      }
    );
  }, [isOpen, effectiveBillingContact?.id, effectiveBillingContact?.vat_number, effectiveBillingContact?.pays, step]);

  // Select all by default when data loads (contact mode only, and NOT when pre-selected)
  useEffect(() => {
    if (searchMode === 'contact' && eligibleAffaires && eligibleAffaires.length > 0 && !hasPreselectedAffaires) {
      setSelectedAffaires(eligibleAffaires.map(a => a.affaire_id));
    }
  }, [eligibleAffaires, searchMode, hasPreselectedAffaires]);

  // Capture affaires snapshot for pre-selected mode (prevents display issues after query refetch)
  useEffect(() => {
    if (hasPreselectedAffaires && eligibleAffaires && eligibleAffaires.length > 0 && !initialAffairesSnapshot) {
      // Only capture once when data first loads
      setInitialAffairesSnapshot([...eligibleAffaires]);
    }
  }, [hasPreselectedAffaires, eligibleAffaires, initialAffairesSnapshot]);

  // Toggle single affaire
  const toggleAffaire = useCallback((affaireId: string) => {
    setSelectedAffaires(prev =>
      prev.includes(affaireId)
        ? prev.filter(id => id !== affaireId)
        : [...prev, affaireId]
    );
  }, []);

  // Toggle all affaires
  const toggleAll = useCallback(() => {
    if (selectedAffaires.length === eligibleAffaires.length) {
      setSelectedAffaires([]);
    } else {
      setSelectedAffaires(eligibleAffaires.map(a => a.affaire_id));
    }
  }, [eligibleAffaires, selectedAffaires.length]);

  // Toggle all affaires for a specific billing contact (client mode)
  const toggleContactAffaires = useCallback((contactId: string, affaires: EligibleAffaire[]) => {
    const contactAffaireIds = affaires.map(a => a.affaire_id);
    const allSelected = contactAffaireIds.every(id => selectedAffaires.includes(id));

    if (allSelected) {
      setSelectedAffaires(prev => prev.filter(id => !contactAffaireIds.includes(id)));
    } else {
      setSelectedAffaires(prev => [...new Set([...prev, ...contactAffaireIds])]);
    }
  }, [selectedAffaires]);

  // Toggle expanded state for billing contact group
  const toggleContactExpanded = useCallback((contactId: string) => {
    setExpandedContacts(prev => {
      const next = new Set(prev);
      if (next.has(contactId)) {
        next.delete(contactId);
      } else {
        next.add(contactId);
      }
      return next;
    });
  }, []);

  // Handle client selection from search
  const handleClientSelect = useCallback((client: { id: string; fmClientId: string; firstName: string; lastName: string; company: string | null }) => {
    const clientName = client.company || `${client.firstName} ${client.lastName}`.trim();
    setSelectedClient({ id: client.fmClientId, name: clientName });
    setStep('select');
    setSelectedAffaires([]);
    setEffectiveBillingContact(null); // Will be determined by affaire selection
    // Expand all billing contacts by default
    setExpandedContacts(new Set());
  }, []);

  // Calculate selected totals
  // Use snapshot for calculations to prevent issues after query refetch
  const affairesForCalculation = initialAffairesSnapshot || eligibleAffaires;

  const selectedTotal = useMemo(() => {
    return affairesForCalculation
      .filter(a => selectedAffaires.includes(a.affaire_id))
      .reduce(
        (sum, a) => ({
          htva: sum.htva + a.total_htva,
          tva: sum.tva + a.total_tva,
          tvac: sum.tvac + a.total_tvac,
        }),
        { htva: 0, tva: 0, tvac: 0 }
      );
  }, [affairesForCalculation, selectedAffaires]);

  // Determine effective billing contact from selection (client mode)
  useEffect(() => {
    if (searchMode === 'client' && selectedBillingContactIds.size === 1 && clientEligibleData?.billing_contacts) {
      const contactId = Array.from(selectedBillingContactIds)[0];
      const bc = clientEligibleData.billing_contacts.find(c => c.billing_contact_id === contactId);
      if (bc) {
        // Create a BillingContact from the group data
        // Now includes VAT, address, email for VIES validation
        setEffectiveBillingContact({
          id: bc.billing_contact_id,
          name: bc.billing_contact_name,
          address: bc.billing_contact_address || null,
          vat_number: bc.billing_contact_vat || null,
          email: bc.billing_contact_email || null,
          pays: bc.billing_contact_pays || null,
        });
      }
    } else if (searchMode === 'client' && selectedBillingContactIds.size !== 1) {
      setEffectiveBillingContact(null);
    }
  }, [searchMode, selectedBillingContactIds, clientEligibleData]);

  // Check if VAT is invalid (separate from blocking to show override UI)
  // Used to display the VAT override checkbox
  const isVatInvalid = useMemo(() => {
    const effectiveVat = getEffectiveVatNumber(effectiveBillingContact?.vat_number);
    if (!effectiveVat) return false;
    return vatValidationResult &&
           !vatValidationResult.isValid &&
           vatValidationResult.errorType !== 'RATE_LIMIT' &&
           vatValidationResult.errorType !== 'API_ERROR' &&
           vatValidationResult.errorType !== 'UNSUPPORTED_COUNTRY';
  }, [effectiveBillingContact?.vat_number, vatValidationResult]);

  // Check if VAT validation blocks generation
  // Placeholder VAT values (NA, N/A, etc.) don't block generation
  // API errors and rate limits also don't block (user can proceed and fix later)
  // User can bypass by checking the override checkbox
  const isVatValidationBlocking = useMemo(() => {
    const effectiveVat = getEffectiveVatNumber(effectiveBillingContact?.vat_number);
    if (!effectiveVat) return false; // No VAT or placeholder = no block
    if (!vatValidationResult) return false;
    // Don't block for rate limits, API errors (validation service issues), or non-EU numbers (not VIES-verifiable)
    if (vatValidationResult.errorType === 'RATE_LIMIT' || vatValidationResult.errorType === 'API_ERROR' || vatValidationResult.errorType === 'UNSUPPORTED_COUNTRY') return false;
    // User can override the VAT validation block
    if (vatOverrideAccepted) return false;
    return !vatValidationResult.isValid;
  }, [effectiveBillingContact?.vat_number, vatValidationResult, vatOverrideAccepted]);

  // Can generate invoice?
  const canGenerate = useMemo(() => {
    if (selectedAffaires.length === 0) return false;
    if (hasMultipleBillingContacts) return false;
    if (isVatValidationBlocking) return false;
    if (!effectiveBillingContact) return false;
    return true;
  }, [selectedAffaires.length, hasMultipleBillingContacts, isVatValidationBlocking, effectiveBillingContact]);

  const handleGenerate = async () => {
    if (!effectiveBillingContact) return;

    setStep('generating');
    try {
      // Include VIES official data if available (for professional customers)
      // This data will be used for the billing name and address on the invoice
      const viesOfficialData = vatValidationResult?.isValid && vatValidationResult.officialData
        ? {
            name: vatValidationResult.officialData.name,
            address: vatValidationResult.officialData.address
          }
        : undefined;

      const invoice = await generateMutation.mutateAsync({
        affaireIds: selectedAffaires,
        billingContactId: effectiveBillingContact.id,
        language: selectedLanguage,
        viesOfficialData,
        bankAccountId: bankAccountId || undefined
      });
      setGeneratedInvoice(invoice as Invoice);
      setStep('preview');
    } catch (error: unknown) {
      setStep('error');
      setErrorMessage(error instanceof Error ? error.message : 'Une erreur est survenue');
    }
  };

  // Handle validate (send to Falco)
  const handleValidate = async () => {
    if (!generatedInvoice) return;

    setIsValidatingFalco(true);
    setStep('finalizing');
    try {
      const result = await validateMutation.mutateAsync({
        id: generatedInvoice.id,
        currentStatus: generatedInvoice.status,
        sendEmail: sendEmailOnValidate
      });

      // Extract email results from response
      const responseData = (result as { data?: { invoice?: Invoice; emailSent?: boolean; emailRecipient?: string | null; emailError?: string | null } })?.data;
      const validatedInvoice = responseData?.invoice;

      if (validatedInvoice) {
        setGeneratedInvoice({
          ...validatedInvoice,
          email_sent: responseData?.emailSent ?? false,
          email_recipient: responseData?.emailRecipient ?? null,
          email_error: responseData?.emailError ?? null
        } as Invoice);
      } else if (result?.invoice) {
        setGeneratedInvoice(result.invoice);
      }

      setStep('success');
      // Note: Don't call onSuccess here - let user see the success step first
      // onSuccess will be called when user closes the modal
    } catch (error: unknown) {
      setStep('error');
      setErrorMessage(error instanceof Error ? error.message : 'Une erreur est survenue lors de l\'envoi');
    } finally {
      setIsValidatingFalco(false);
    }
  };

  // Go back to selection from preview
  const handleBackToSelect = () => {
    setStep('select');
    setGeneratedInvoice(null);
  };

  // Go back to search from selection
  const handleBackToSearch = () => {
    setStep('search');
    setSelectedClient(null);
    setSelectedAffaires([]);
    setEffectiveBillingContact(null);
  };

  return (
    <>
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog
        onClose={onClose}
        className="relative z-50"
        // Focus trap is automatically enabled by HeadlessUI Dialog
      >
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all">
                {/* Header - Focus trap is automatically handled by HeadlessUI Dialog */}
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <DocumentTextIcon className="h-6 w-6 text-[#ef5343]" />
                    <div>
                      <Dialog.Title className="text-lg font-semibold text-gray-900">
                        Facture multi-affaires
                      </Dialog.Title>
                      <p className="text-sm text-gray-500">
                        {step === 'search' && 'Rechercher un client'}
                        {step === 'select' && (searchMode === 'client' ? selectedClient?.name : effectiveBillingContact?.name)}
                        {step === 'preview' && 'Verification avant envoi'}
                        {step === 'generating' && 'Generation en cours...'}
                        {step === 'finalizing' && 'Envoi en cours...'}
                        {step === 'success' && 'Facture envoyee'}
                        {step === 'error' && 'Erreur'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="facturation-btn-modal-close"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Search step (client mode only) */}
                  {step === 'search' && (
                    <div className="space-y-6">
                      {/* Search input */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Rechercher un client
                        </label>
                        <div className="relative">
                          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <input
                            type="text"
                            value={clientSearch.searchQuery}
                            onChange={(e) => clientSearch.search(e.target.value)}
                            placeholder="Nom, prenom ou societe..."
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ef5343] focus:border-transparent"
                            autoFocus
                          />
                        </div>
                      </div>

                      {/* Search loading */}
                      {clientSearch.loading && (
                        <div className="text-center py-4">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#ef5343] mx-auto"></div>
                          <p className="mt-2 text-sm text-gray-500">Recherche en cours...</p>
                        </div>
                      )}

                      {/* Search results */}
                      {!clientSearch.loading && clientSearch.hasSearched && (
                        <div>
                          {clientSearch.clients.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                              <UsersIcon className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                              <p>Aucun client trouve</p>
                            </div>
                          ) : (
                            <div className="border border-gray-200 rounded-lg max-h-80 overflow-y-auto">
                              {clientSearch.clients.map((client) => (
                                <button
                                  key={client.id}
                                  onClick={() => handleClientSelect(client)}
                                  className="w-full flex items-center gap-3 px-4 py-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors text-left"
                                >
                                  <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                                    <UserIcon className="h-5 w-5 text-gray-500" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-gray-900 truncate">
                                      {client.company || `${client.firstName} ${client.lastName}`}
                                    </p>
                                    {client.company && (
                                      <p className="text-sm text-gray-500 truncate">
                                        {client.firstName} {client.lastName}
                                      </p>
                                    )}
                                    {client.email && (
                                      <p className="text-xs text-gray-400 truncate">{client.email}</p>
                                    )}
                                  </div>
                                  <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Initial state */}
                      {!clientSearch.loading && !clientSearch.hasSearched && (
                        <div className="text-center py-8 text-gray-500">
                          <MagnifyingGlassIcon className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                          <p>Commencez a taper pour rechercher un client</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Loading state for affaires */}
                  {step === 'select' && isLoadingEligible && (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#ef5343] mx-auto"></div>
                      <p className="mt-4 text-gray-600">Chargement des affaires eligibles...</p>
                    </div>
                  )}

                  {/* No eligible affaires */}
                  {step === 'select' && !isLoadingEligible && eligibleAffaires.length === 0 && (
                    <div className="bg-yellow-50 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600 flex-shrink-0" />
                        <div>
                          <h3 className="font-medium text-yellow-900">Aucune affaire eligible</h3>
                          <p className="text-sm text-yellow-700 mt-1">
                            {searchMode === 'client'
                              ? 'Ce client n\'a pas d\'affaires archivees avec produits a facturer.'
                              : 'Il n\'y a pas d\'affaires archivees avec produits pour ce contact.'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Selection step */}
                  {step === 'select' && !isLoadingEligible && eligibleAffaires.length > 0 && (
                    <div className="space-y-6">
                      {/* Multi-billing contact warning (client mode only) */}
                      {hasMultipleBillingContacts && (
                        <div className="bg-red-50 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <ExclamationTriangleIcon className="h-6 w-6 text-red-600 flex-shrink-0" />
                            <div>
                              <h3 className="font-medium text-red-900">Contacts de facturation differents</h3>
                              <p className="text-sm text-red-700 mt-1">
                                Les affaires selectionnees appartiennent a des contacts de facturation differents.
                                Une facture ne peut regrouper que des affaires du meme contact.
                              </p>
                              <p className="text-xs text-red-600 mt-2">
                                Veuillez selectionner uniquement des affaires d'un seul contact de facturation.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Billing contact info (contact mode) */}
                      {searchMode === 'contact' && effectiveBillingContact && (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Contact de facturation</h4>
                          <div className="text-sm text-gray-600">
                            <p className="font-medium text-gray-900">{effectiveBillingContact.name}</p>
                            {effectiveBillingContact.address && <p>{effectiveBillingContact.address}</p>}
                            {effectiveBillingContact.vat_number && (
                              <p className="font-mono text-xs mt-1">{effectiveBillingContact.vat_number}</p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* VAT validation (when contact has real VAT, not placeholder) */}
                      {getEffectiveVatNumber(effectiveBillingContact?.vat_number) && (
                        <VatStatusSection
                          vatNumber={effectiveBillingContact!.vat_number!}
                          isValidating={isVatValidating}
                          vatValidationResult={vatValidationResult}
                          peppolStatus={peppolStatus}
                          billingContact={effectiveBillingContact ? {
                            name: effectiveBillingContact.name,
                            address: effectiveBillingContact.address,
                            vat_number: effectiveBillingContact.vat_number
                          } : null}
                          showDetailedComparison={true}
                        />
                      )}

                      {/* VAT Validation error - Invalid VAT with override option */}
                      {isVatInvalid && (
                        <div className={`rounded-lg p-4 ${vatOverrideAccepted ? 'bg-yellow-50' : 'bg-red-50'}`}>
                          <div className="flex items-start gap-3">
                            <ExclamationTriangleIcon className={`h-6 w-6 flex-shrink-0 ${vatOverrideAccepted ? 'text-yellow-600' : 'text-red-600'}`} />
                            <div className="flex-1">
                              <h3 className={`font-medium ${vatOverrideAccepted ? 'text-yellow-900' : 'text-red-900'}`}>
                                Numero de TVA invalide
                              </h3>
                              <p className={`text-sm mt-1 ${vatOverrideAccepted ? 'text-yellow-700' : 'text-red-700'}`}>
                                Le numero de TVA <span className="font-mono">{effectiveBillingContact?.vat_number}</span> n'est pas enregistre dans le service VIES.
                                {vatValidationResult?.error && (
                                  <span className="block mt-1">{vatValidationResult.error}</span>
                                )}
                              </p>
                              {!vatOverrideAccepted && (
                                <p className="text-xs text-red-600 mt-2">
                                  Veuillez corriger le numero de TVA dans la fiche client avant de generer la facture.
                                </p>
                              )}
                              <label className="flex items-center gap-2 mt-3 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={vatOverrideAccepted}
                                  onChange={(e) => setVatOverrideAccepted(e.target.checked)}
                                  className="rounded border-gray-300 text-[#ef5343] focus:ring-[#ef5343]"
                                />
                                <span className={`text-sm ${vatOverrideAccepted ? 'text-yellow-800 font-medium' : 'text-red-700'}`}>
                                  Je confirme vouloir generer la facture malgre le numero de TVA non valide
                                </span>
                              </label>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Peppol status (only if no real VAT - otherwise shown in VatStatusSection) */}
                      {peppolStatus && !getEffectiveVatNumber(effectiveBillingContact?.vat_number) && (
                        <div className={`rounded-lg p-3 ${peppolStatus.peppolRegistered ? 'bg-blue-50' : 'bg-gray-50'}`}>
                          <div className="flex items-center gap-2">
                            <GlobeAltIcon className={`h-5 w-5 ${peppolStatus.peppolRegistered ? 'text-blue-600' : 'text-gray-400'}`} />
                            <span className={`text-sm ${peppolStatus.peppolRegistered ? 'text-blue-700' : 'text-gray-600'}`}>
                              {peppolStatus.peppolRegistered ? 'Envoi via Peppol' : 'Envoi par email'}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Affaires selection - Contact mode (flat list) - ONLY show if no pre-selected affaires */}
                      {searchMode === 'contact' && !hasPreselectedAffaires && (
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <label className="text-sm font-medium text-gray-700">
                              Affaires a inclure ({selectedAffaires.length}/{eligibleAffaires.length})
                            </label>
                            <button
                              onClick={toggleAll}
                              className="facturation-btn-text-link"
                            >
                              {selectedAffaires.length === eligibleAffaires.length ? 'Deselectionner tout' : 'Selectionner tout'}
                            </button>
                          </div>
                          <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
                            {eligibleAffaires.map((affaire) => (
                              <label
                                key={affaire.affaire_id}
                                className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedAffaires.includes(affaire.affaire_id)}
                                  onChange={() => toggleAffaire(affaire.affaire_id)}
                                  className="rounded border-gray-300 text-[#ef5343] focus:ring-[#ef5343]"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-gray-900">
                                      {affaire.k_id_dossier}
                                    </span>
                                  </div>
                                  <p className="text-xs text-gray-500 truncate">
                                    {affaire.adresse_bien}
                                  </p>
                                </div>
                                <span className="text-sm font-medium text-gray-700">
                                  {formatCurrency(affaire.total_tvac)}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Affaires selection - Client mode (grouped by billing contact) */}
                      {searchMode === 'client' && clientEligibleData?.billing_contacts && (
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <label className="text-sm font-medium text-gray-700">
                              Affaires a inclure ({selectedAffaires.length}/{eligibleAffaires.length})
                            </label>
                            <button
                              onClick={toggleAll}
                              className="facturation-btn-text-link"
                            >
                              {selectedAffaires.length === eligibleAffaires.length ? 'Deselectionner tout' : 'Selectionner tout'}
                            </button>
                          </div>
                          <div className="border border-gray-200 rounded-lg max-h-80 overflow-y-auto">
                            {clientEligibleData.billing_contacts.map((bc) => {
                              const isExpanded = expandedContacts.has(bc.billing_contact_id);
                              const contactAffaireIds = bc.affaires.map(a => a.affaire_id);
                              const selectedCount = contactAffaireIds.filter(id => selectedAffaires.includes(id)).length;
                              const allSelected = selectedCount === bc.affaires.length;

                              return (
                                <div key={bc.billing_contact_id} className="border-b border-gray-200 last:border-b-0">
                                  {/* Contact header */}
                                  <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors">
                                    <button
                                      onClick={() => toggleContactExpanded(bc.billing_contact_id)}
                                      className="p-1 hover:bg-gray-200 rounded"
                                    >
                                      {isExpanded ? (
                                        <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                                      ) : (
                                        <ChevronRightIcon className="h-4 w-4 text-gray-500" />
                                      )}
                                    </button>
                                    <input
                                      type="checkbox"
                                      checked={allSelected && bc.affaires.length > 0}
                                      onChange={() => toggleContactAffaires(bc.billing_contact_id, bc.affaires)}
                                      className="rounded border-gray-300 text-[#ef5343] focus:ring-[#ef5343]"
                                    />
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-gray-900 truncate">
                                        {bc.billing_contact_name}
                                      </p>
                                      <p className="text-xs text-gray-500">
                                        {bc.billing_contact_role} - {bc.affaires.length} affaire{bc.affaires.length > 1 ? 's' : ''}
                                        {selectedCount > 0 && ` (${selectedCount} selectionnee${selectedCount > 1 ? 's' : ''})`}
                                      </p>
                                    </div>
                                    <span className="text-sm font-medium text-gray-700">
                                      {formatCurrency(bc.totals.tvac)}
                                    </span>
                                  </div>

                                  {/* Affaires list (collapsible) */}
                                  {isExpanded && (
                                    <div className="pl-10">
                                      {bc.affaires.map((affaire) => (
                                        <label
                                          key={affaire.affaire_id}
                                          className="flex items-center gap-3 px-4 py-2 border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
                                        >
                                          <input
                                            type="checkbox"
                                            checked={selectedAffaires.includes(affaire.affaire_id)}
                                            onChange={() => toggleAffaire(affaire.affaire_id)}
                                            className="rounded border-gray-300 text-[#ef5343] focus:ring-[#ef5343]"
                                          />
                                          <div className="flex-1 min-w-0">
                                            <span className="text-sm font-medium text-gray-900">
                                              {affaire.k_id_dossier}
                                            </span>
                                            <p className="text-xs text-gray-500 truncate">
                                              {affaire.adresse_bien}
                                            </p>
                                          </div>
                                          <span className="text-sm font-medium text-gray-600">
                                            {formatCurrency(affaire.total_tvac)}
                                          </span>
                                        </label>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Pre-selected affaires summary (read-only, from GroupFacturationView) */}
                      {hasPreselectedAffaires && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-3">
                            Affaires selectionnees ({selectedAffaires.length})
                          </h4>
                          <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto bg-white">
                            {/* Use snapshot to prevent display issues after query refetch */}
                            {(initialAffairesSnapshot || eligibleAffaires)
                              .filter(a => selectedAffaires.includes(a.affaire_id))
                              .map((affaire) => (
                                <div
                                  key={affaire.affaire_id}
                                  className="flex items-center justify-between px-4 py-2 border-b border-gray-100 last:border-b-0"
                                >
                                  <div className="flex-1 min-w-0">
                                    <span className="text-sm font-medium text-gray-900">{affaire.k_id_dossier}</span>
                                    <p className="text-xs text-gray-500 truncate">{affaire.adresse_bien}</p>
                                  </div>
                                  <span className="text-sm font-medium text-gray-700 ml-4">
                                    {formatCurrency(affaire.total_tvac)}
                                  </span>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}

                      {/* Bank account selector */}
                      {bankAccounts.length > 0 && (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Compte bancaire</h4>
                          <select
                            value={bankAccountId || ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === '__add__') {
                                setShowAddAccount(true);
                              } else {
                                setBankAccountId(Number(val));
                                setShowAddAccount(false);
                              }
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-[#ef5343] focus:border-[#ef5343]"
                          >
                            {bankAccounts.map((account: BankAccount) => (
                              <option key={account.id} value={account.id}>
                                {account.label} — {account.iban.replace(/(.{4})/g, '$1 ').trim()}
                              </option>
                            ))}
                            <option value="__add__">+ Ajouter un compte</option>
                          </select>
                          {showAddAccount && (
                            <div className="mt-3 p-3 bg-white rounded-lg border border-gray-200 space-y-2">
                              <input
                                type="text"
                                placeholder="Libelle (ex: BNP Secondaire)"
                                value={newAccountLabel}
                                onChange={(e) => setNewAccountLabel(e.target.value)}
                                className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm"
                              />
                              <input
                                type="text"
                                placeholder="IBAN (ex: BE72652837171316)"
                                value={newAccountIban}
                                onChange={(e) => setNewAccountIban(e.target.value)}
                                className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm font-mono"
                              />
                              <input
                                type="text"
                                placeholder="BIC (ex: BBRUBEBB)"
                                value={newAccountBic}
                                onChange={(e) => setNewAccountBic(e.target.value)}
                                className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm font-mono"
                              />
                              <div className="flex gap-2 pt-1">
                                <button
                                  type="button"
                                  onClick={async () => {
                                    if (!newAccountLabel || !newAccountIban || !newAccountBic) return;
                                    try {
                                      const created = await addBankAccountMutation.mutateAsync({
                                        label: newAccountLabel,
                                        iban: newAccountIban,
                                        bic: newAccountBic,
                                      });
                                      setBankAccountId(created.id);
                                      setShowAddAccount(false);
                                      setNewAccountLabel('');
                                      setNewAccountIban('');
                                      setNewAccountBic('');
                                    } catch {
                                      // mutation error handled by React Query
                                    }
                                  }}
                                  disabled={!newAccountLabel || !newAccountIban || !newAccountBic || addBankAccountMutation.isPending}
                                  className="px-3 py-1.5 text-xs font-medium bg-[#ef5343] text-white rounded hover:bg-[#d94838] disabled:opacity-50"
                                >
                                  {addBankAccountMutation.isPending ? 'Ajout...' : 'Ajouter'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setShowAddAccount(false);
                                    const defaultAccount = bankAccounts.find((a: BankAccount) => a.is_default);
                                    setBankAccountId(defaultAccount?.id || bankAccounts[0]?.id || null);
                                  }}
                                  className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
                                >
                                  Annuler
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Selected total */}
                      {selectedAffaires.length > 0 && (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="text-sm font-medium text-gray-700 mb-3">Total selectionne</h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">HTVA:</span>
                              <span>{formatCurrency(selectedTotal.htva)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">TVA:</span>
                              <span>{formatCurrency(selectedTotal.tva)}</span>
                            </div>
                            <div className="flex justify-between font-semibold border-t pt-2">
                              <span>TVAC:</span>
                              <span className="text-[#ef5343]">{formatCurrency(selectedTotal.tvac)}</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Next invoice number */}
                      {nextNumber && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <InformationCircleIcon className="h-4 w-4" />
                          <span>Prochain numero: <span className="font-mono font-medium">{nextNumber}</span></span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Generating state */}
                  {step === 'generating' && (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ef5343] mx-auto"></div>
                      <p className="mt-4 text-gray-600">Generation de la facture en cours...</p>
                      <p className="text-sm text-gray-500">
                        {selectedAffaires.length} affaire{selectedAffaires.length > 1 ? 's' : ''} en cours de traitement
                      </p>
                    </div>
                  )}

                  {/* Preview state - Invoice generated, review before sending to Falco */}
                  {step === 'preview' && generatedInvoice && (
                    <div className="space-y-6">
                      {/* Success message */}
                      <div className="bg-green-50 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0" />
                          <div>
                            <h3 className="font-medium text-green-900">
                              {generatedInvoice.invoice_number ? 'Facture generee' : 'Brouillon cree'}
                            </h3>
                            <p className="text-sm text-green-700 mt-1">
                              {generatedInvoice.invoice_number
                                ? <>La facture <span className="font-mono font-medium">{generatedInvoice.invoice_number}</span> a ete generee avec succes.</>
                                : <>Le brouillon a ete cree. Cliquez sur "Valider" pour attribuer un numero et envoyer a Falco.</>
                              }
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Draft warning */}
                      {!generatedInvoice.invoice_number && (
                        <div className="fact-draft-warning">
                          <p>
                            <strong>Brouillon</strong> - Le numero de facture sera attribue lors de la validation.
                          </p>
                        </div>
                      )}

                      {/* Invoice summary */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Resume de la facture</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Numero:</span>
                            <span className="font-mono font-medium">{generatedInvoice.invoice_number || 'Brouillon'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Client:</span>
                            <span>{effectiveBillingContact?.name}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Nombre d'affaires:</span>
                            <span>{selectedAffaires.length} affaire{selectedAffaires.length > 1 ? 's' : ''}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Montant HTVA:</span>
                            <span>{formatCurrency(generatedInvoice.total_htva)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">TVA:</span>
                            <span>{formatCurrency(generatedInvoice.total_tva)}</span>
                          </div>
                          <div className="flex justify-between font-medium text-gray-900 border-t pt-2">
                            <span>Total TVAC:</span>
                            <span className="text-[#ef5343]">{formatCurrency(generatedInvoice.total_tvac)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Affaires included */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Affaires incluses</h4>
                        <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto bg-white">
                          {/* Use snapshot to prevent display issues after query refetch */}
                          {((initialAffairesSnapshot || eligibleAffaires) as EligibleAffaire[])?.filter(
                            (a: EligibleAffaire) => selectedAffaires.includes(a.affaire_id)
                          ).map((affaire: EligibleAffaire) => (
                            <div
                              key={affaire.affaire_id}
                              className="flex items-center justify-between px-4 py-2 border-b border-gray-100 last:border-b-0"
                            >
                              <div className="flex-1 min-w-0">
                                <span className="text-sm font-medium text-gray-900">{affaire.k_id_dossier}</span>
                                <p className="text-xs text-gray-500 truncate">{affaire.adresse_bien}</p>
                              </div>
                              <div className="text-right ml-4">
                                <span className="text-sm font-medium text-gray-700">{formatCurrency(affaire.total_tvac)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Billing contact info */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Contact de facturation</h4>
                        <div className="text-sm text-gray-600">
                          <p className="font-medium text-gray-900">{effectiveBillingContact?.name}</p>
                          {effectiveBillingContact?.address && <p>{effectiveBillingContact.address}</p>}
                          {effectiveBillingContact?.vat_number && (
                            <p className="font-mono text-xs mt-1">{effectiveBillingContact.vat_number}</p>
                          )}
                          {effectiveBillingContact?.email && (
                            <p className="text-xs mt-1">{effectiveBillingContact.email}</p>
                          )}
                        </div>
                      </div>

                      {/* Email option */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={sendEmailOnValidate}
                            onChange={(e) => setSendEmailOnValidate(e.target.checked)}
                            disabled={isValidatingFalco}
                            className="fact-email-checkbox"
                          />
                          <span className="text-sm text-gray-700">
                            Envoyer la facture par email au client
                          </span>
                        </label>
                        {effectiveBillingContact?.email && (
                          <p className="text-xs text-gray-500 mt-1 ml-7">
                            Email: {effectiveBillingContact.email}
                          </p>
                        )}
                      </div>

                      {/* PDF preview */}
                      <div className="flex justify-center">
                        {generatedInvoice.pdf_path ? (
                          <button
                            onClick={() => {
                              setIsPdfPreviewMode(false);
                              setShowPdfPreview(true);
                            }}
                            disabled={isValidatingFalco}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                          >
                            <EyeIcon className="h-5 w-5" />
                            Voir le PDF
                          </button>
                        ) : (
                          <button
                            onClick={() => {
                              setIsPdfPreviewMode(true);
                              setShowPdfPreview(true);
                            }}
                            disabled={isValidatingFalco}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200 transition-colors disabled:opacity-50"
                          >
                            <EyeIcon className="h-5 w-5" />
                            Previsualiser le brouillon
                          </button>
                        )}
                      </div>

                      {/* Next step info */}
                      <div className="bg-blue-50 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <PaperAirplaneIcon className="h-6 w-6 text-blue-600 flex-shrink-0" />
                          <div>
                            <h3 className="font-medium text-blue-900">Prochaine etape</h3>
                            <p className="text-sm text-blue-700 mt-1">
                              Verifiez le PDF puis cliquez sur "Valider et envoyer" pour transmettre la facture a Falco.
                              {effectiveBillingContact?.email && sendEmailOnValidate && (
                                <span className="block mt-1">
                                  La facture sera egalement envoyee par email a <span className="font-medium">{effectiveBillingContact.email}</span>.
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Finalizing state */}
                  {step === 'finalizing' && (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ef5343] mx-auto"></div>
                      <p className="mt-4 text-gray-600">Envoi de la facture a Falco...</p>
                    </div>
                  )}

                  {/* Success state */}
                  {step === 'success' && (
                    <div className="text-center py-8">
                      <CheckCircleIcon className="h-12 w-12 text-green-600 mx-auto" />
                      <h3 className="mt-4 text-lg font-semibold text-gray-900">Facture envoyee</h3>
                      <p className="mt-2 text-gray-600">
                        La facture {generatedInvoice?.invoice_number || '(numero non disponible)'} regroupant {selectedAffaires.length} affaire{selectedAffaires.length > 1 ? 's' : ''} a ete envoyee a Falco avec succes.
                      </p>

                      {/* Email status */}
                      {(generatedInvoice as Invoice & { email_sent?: boolean; email_recipient?: string | null; email_error?: string | null })?.email_sent ? (
                        <div className="mt-4 mx-auto max-w-md bg-green-50 rounded-lg p-3">
                          <div className="flex items-center gap-2 justify-center">
                            <EnvelopeIcon className="h-5 w-5 text-green-600" />
                            <div className="text-left">
                              <p className="text-sm font-medium text-green-800">Email envoye</p>
                              <p className="text-xs text-green-600">
                                La facture a ete envoyee a {(generatedInvoice as Invoice & { email_recipient?: string | null })?.email_recipient}
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : (generatedInvoice as Invoice & { email_error?: string | null })?.email_error ? (
                        <div className="mt-4 mx-auto max-w-md bg-amber-50 rounded-lg p-3">
                          <div className="flex items-center gap-2 justify-center">
                            <ExclamationTriangleIcon className="h-5 w-5 text-amber-600" />
                            <div className="text-left">
                              <p className="text-sm font-medium text-amber-800">Email non envoye</p>
                              <p className="text-xs text-amber-600">{(generatedInvoice as Invoice & { email_error?: string | null })?.email_error}</p>
                            </div>
                          </div>
                        </div>
                      ) : null}

                      <div className="fact-success-actions">
                        {generatedInvoice && generatedInvoice.pdf_path ? (
                          <button
                            onClick={async () => {
                              try {
                                const blob = await downloadInvoicePdf(generatedInvoice.id);
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = generatedInvoice.invoice_number
                                  ? `facture_${generatedInvoice.invoice_number.replace(/\//g, '-')}.pdf`
                                  : `facture_brouillon_${generatedInvoice.id}.pdf`;
                                a.click();
                                setTimeout(() => window.URL.revokeObjectURL(url), 100);
                              } catch (error) {
                                console.error('Download failed:', error);
                              }
                            }}
                            className="fact-success-download-btn"
                          >
                            <ArrowDownTrayIcon className="fact-success-download-btn-icon" />
                            Telecharger le PDF
                          </button>
                        ) : (
                          <p className="fact-success-pdf-warning">
                            {generatedInvoice ? 'PDF non disponible (generation en cours ou echouee)' : 'Donnees de facture non disponibles'}
                          </p>
                        )}
                        <button
                          onClick={() => {
                            onSuccess?.();
                            onClose();
                          }}
                          className="fact-success-close-btn"
                        >
                          Fermer
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Error state */}
                  {step === 'error' && (
                    <div className="text-center py-8">
                      <ExclamationTriangleIcon className="h-12 w-12 text-red-600 mx-auto" />
                      <h3 className="mt-4 text-lg font-semibold text-gray-900">Erreur</h3>
                      <p className="mt-2 text-red-600">{errorMessage}</p>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                  {/* Language selection badges (only in select step) */}
                  {step === 'select' && (
                    <div className="flex items-center gap-1">
                      {languages.map((lang) => (
                        <button
                          key={lang.code}
                          type="button"
                          onClick={() => setSelectedLanguage(lang.code)}
                          className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                            selectedLanguage === lang.code
                              ? 'bg-[#ef5343] text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {lang.code.toUpperCase()}
                        </button>
                      ))}
                    </div>
                  )}
                  {(step === 'search' || step === 'success' || step === 'error' || step === 'generating' || step === 'preview' || step === 'finalizing') && <div />}

                  {/* Action buttons */}
                  <div className="flex gap-3">
                    {/* Search step */}
                    {step === 'search' && (
                      <button
                        onClick={onClose}
                        className="facturation-btn-modal-cancel"
                      >
                        Annuler
                      </button>
                    )}

                    {/* Select step */}
                    {step === 'select' && (
                      <>
                        {searchMode === 'client' && (
                          <button
                            onClick={handleBackToSearch}
                            className="facturation-btn-modal-cancel inline-flex items-center gap-2"
                          >
                            <ArrowLeftIcon className="h-4 w-4" />
                            Retour
                          </button>
                        )}
                        {searchMode === 'contact' && (
                          <button
                            onClick={onClose}
                            className="facturation-btn-modal-cancel"
                          >
                            Annuler
                          </button>
                        )}
                        <button
                          onClick={handleGenerate}
                          disabled={!canGenerate}
                          className="facturation-btn-modal-primary disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Generer la facture
                        </button>
                      </>
                    )}

                    {/* Preview step */}
                    {step === 'preview' && (
                      <>
                        <button
                          onClick={handleBackToSelect}
                          disabled={isValidatingFalco}
                          className="facturation-btn-modal-cancel inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ArrowLeftIcon className="h-4 w-4" />
                          Retour
                        </button>
                        <button
                          onClick={handleValidate}
                          disabled={isValidatingFalco}
                          className="facturation-btn-modal-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isValidatingFalco ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              Envoi en cours...
                            </>
                          ) : (
                            <>
                              <PaperAirplaneIcon className="h-4 w-4" />
                              Valider et envoyer a Falco
                            </>
                          )}
                        </button>
                      </>
                    )}

                    {/* Success/Error step */}
                    {(step === 'success' || step === 'error') && (
                      <button
                        onClick={onClose}
                        className="facturation-btn-modal-primary"
                      >
                        Fermer
                      </button>
                    )}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>

    {/* PDF Preview Modal */}
    {showPdfPreview && generatedInvoice && (
      <PdfPreviewModal
        document={{
          type: 'invoice',
          id: generatedInvoice.id,
          number: generatedInvoice.invoice_number || 'Brouillon',
          date: generatedInvoice.invoice_date,
          clientName: effectiveBillingContact?.name || '-',
          amount: generatedInvoice.total_tvac,
          status: generatedInvoice.status,
          hasPdf: !!generatedInvoice.pdf_path || isPdfPreviewMode,
        }}
        onClose={() => {
          setShowPdfPreview(false);
          setIsPdfPreviewMode(false);
        }}
        onDownload={async () => {
          try {
            const blob = await downloadInvoicePdf(generatedInvoice.id);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = generatedInvoice.invoice_number
              ? `facture_${generatedInvoice.invoice_number.replace(/\//g, '-')}.pdf`
              : `facture_brouillon_${generatedInvoice.id}.pdf`;
            a.click();
            setTimeout(() => window.URL.revokeObjectURL(url), 100);
          } catch (error) {
            console.error('Download failed:', error);
          }
        }}
        previewMode={isPdfPreviewMode}
        onBack={() => {
          setShowPdfPreview(false);
          setIsPdfPreviewMode(false);
        }}
        backLabel="Retour a la validation"
      />
    )}
  </>
  );
};

export default GenerateMonthlyInvoiceModal;
